/**
 * Alternate LZ (04 §8): the second extraction point.
 *
 * ROUND5 found the card writing a line in the briefing and a second X on the briefing schematic over a
 * sim that had exactly one `pickupX, pickupZ` - seven RP for a picture of a place that did not exist.
 * The point is a real one now: the generator puts it on the ground, `validate/routes.ts` holds it to the
 * same standing and reachability rules as the first, and the Osprey will fly to either (`sim/conditions.ts`).
 *
 * Where it goes is decided, not rolled: the far corner of the objective's OWN ground, as far from the
 * first X as that ground allows. The first X is always set on the quiet flank beside the compound
 * (`plan.ts`), so the furthest open cell is reliably the other end of that flank or the ground beyond
 * the compound's north face - which is what an alternate LZ is for. Nothing here draws on the rng, so a
 * mission is the same map with the card as without it, exactly as Tag Recovery is.
 */

import type { MissionDef } from '../sim/types';
import { NO_STAND } from './builder';
import { FLAG } from './grid';
import { MAP } from './tuning';
import type { GenCtx, Point } from './types';

/** How far apart the two X's must be to be a choice at all rather than two names for one clearing. */
const MIN_APART = 22;

/** Clear ground the Osprey needs to hold its hover and drop the ladder. */
const CLEARING = 2.5;

/** How far south of the compound still counts as the objective's ground, rather than a hike home. */
const SOUTH_REACH = 30;

/** Elbow room round the compound: an LZ against the wire is inside the garrison's lap. */
const COMPOUND_STANDOFF = 4;

/**
 * Put the second X down, or say why there was nowhere for it. `undefined` without the card - a mission
 * flown without Alternate LZ carries no second point at all, so nothing downstream has to ask twice.
 */
export function placeAlternateLz(ctx: GenCtx): MissionDef['pickupAlt'] {
  if (!ctx.params.intel.includes('alternate-lz')) return undefined;
  const pickup: Point = ctx.plan.pickup;
  const c = ctx.plan.compound;
  // The road is a road, not a landing ground, and `keep` is everything another step has spoken for.
  const avoid = NO_STAND | FLAG.road | FLAG.keep;
  const southLimit = Math.min(c.z1 + SOUTH_REACH, ctx.sizeZ - 2);
  let best: Point | null = null;
  let bestAway = MIN_APART * MIN_APART;
  for (let z = 2.5; z <= southLimit; z += 1) {
    for (let x = MAP.edge + 0.5; x <= ctx.sizeX - MAP.edge; x += 1) {
      if (x > c.x0 - COMPOUND_STANDOFF && x < c.x1 + COMPOUND_STANDOFF
        && z > c.z0 - COMPOUND_STANDOFF && z < c.z1 + COMPOUND_STANDOFF) continue;
      const away = (x - pickup.x) * (x - pickup.x) + (z - pickup.z) * (z - pickup.z);
      if (away <= bestAway) continue;
      if (ctx.map.mask.hitsCircle(x, z, CLEARING, avoid)) continue;
      bestAway = away;
      best = { x, z };
    }
  }
  // A failed attempt is re-rolled, as it is for the field grave: better a different map than a card
  // that was paid for and quietly did nothing, which is the whole of what this round is about.
  if (best === null) ctx.map.problems.push('alternate lz: nowhere to set the second extraction point');
  return best ?? undefined;
}
